import * as R from 'ramda'
import * as React from 'react'
import * as ReactRedux from 'react-redux'
import * as ReactRouter from 'react-router-dom'

import * as Common from '@rushplay/common'
import { lazy as loadable } from '@loadable/component'

import * as Suspense from '../suspense'
import * as Configuration from '../configuration'
import { BriteLoginDrawer } from '../brite-login-drawer'
import { Footer } from '../footer'
import { Home } from '../home'
import { LoginDrawer } from '../login-drawer'
import { PrivateRoute } from '../private-route'
import { PromotionsDrawer } from '../promotions-drawer'
import { QuickNavigationMenu } from '../quick-navigation-menu'
import { Registration } from '../registration'
import { SideMenu } from '../menu-drawer'
import { SportsbookDirect } from '../sportsbook-direct'
import { Wallet } from '../wallet'
import { useCookieConsent } from '../use-cookie-consent'
import { useScrollToTop } from '../use-scroll-to-top'

const Account = loadable(() => import('../account'))
const Casino = loadable(() => import('../casino'))
const DevTools = loadable(() => import('../dev-tools'))
const ExpertsArea = loadable(() => import('../experts-area'))
const Faq = loadable(() => import('../faq'))
const GameInfoPage = loadable(() => import('../game-info-page'))
const LiveCasino = loadable(() => import('../live-casino'))
const Logout = loadable(() => import('../logout'))
const NotFound = loadable(() => import('../not-found'))
const PasswordRecovery = loadable(() => import('../password-recovery'))
const PasswordReset = loadable(() => import('../password-reset'))
const PromotionTermsAndConditionsPage = loadable(() =>
  import('../promotion-terms-and-conditions-page')
)
const PromotionsPage = loadable(() => import('../promotions-page'))
const StaticPage = loadable(() => import('../static-page'))
const TermsAndConditions = loadable(() => import('../terms-and-conditions'))

export function Primary() {
  const [ref, handleScrollToTop] = useScrollToTop()
  const location = ReactRouter.useLocation()
  const cookieConsentEnabled = useCookieConsent()
  const expertBlogEnabled = ReactRedux.useSelector(state =>
    Configuration.expertBlogEnabled(state.configuration)
  )

  return (
    <React.Fragment>
      <ReactRouter.Route path="/" component={SideMenu} />
      <Common.Box ref={ref}>
        <Common.Box
          minHeight={[
            'calc(var(--window-inner-height, 100vh) - 46px)',
            null,
            '100vh',
          ]}
          display="flex"
        >
          <Suspense.Boundary>
            <ReactRouter.Switch>
              <ReactRouter.Route exact path="/">
                <Home onScrollToTop={handleScrollToTop} />
              </ReactRouter.Route>

              <ReactRouter.Redirect
                from="/locks"
                to={{ pathname: '/', search: location.search }}
              />

              <ReactRouter.Route
                path="/casino/games/:gameId"
                component={GameInfoPage}
              />

              <ReactRouter.Route path="/casino" component={Casino} />

              <ReactRouter.Route path="/sports" component={SportsbookDirect} />

              <PrivateRoute path="/account" component={Account} />

              <ReactRouter.Route path="/live-casino" component={LiveCasino} />

              <ReactRouter.Route
                path="/promotions-page/:type"
                exact
                component={PromotionsPage}
              />

              <ReactRouter.Redirect
                exact
                from="/promotions-page"
                to="/promotions-page/all"
              />

              {expertBlogEnabled && (
                <ReactRouter.Route
                  path="/experts-area"
                  component={ExpertsArea}
                />
              )}

              <ReactRouter.Route
                exact
                path="/password-recovery"
                component={PasswordRecovery}
              />

              <ReactRouter.Redirect
                exact
                from="/password/:token"
                to="/password-reset/:token"
              />
              <ReactRouter.Route
                path="/password-reset/:token"
                component={PasswordReset}
              />

              <ReactRouter.Route
                path={R.filter(Boolean, [
                  '/(about)',
                  cookieConsentEnabled && '/(cookie-policy)',
                  '/(privacy-policy)',
                  '/(responsible-gaming)',
                  '/(dispute-resolution)',
                  '/(anti-money-laundering)',
                  '/(fairness-rng-testing-methods)',
                  '/(kyc-policies)',
                  '/(self-exclusion)',
                  '/(security)',
                  '/(accounts-payouts-bonuses)',
                  '/(provably-fair)',
                  '/(payments)',
                ])}
                component={StaticPage}
              />

              <ReactRouter.Route
                path="/promotions/:key"
                component={PromotionTermsAndConditionsPage}
              />

              <ReactRouter.Route path="/faq" component={Faq} />

              <ReactRouter.Route
                path="/terms-and-conditions"
                component={TermsAndConditions}
              />

              <ReactRouter.Route path="/logout" component={Logout} />

              <ReactRouter.Route path="/dev-tools">
                <DevTools />
              </ReactRouter.Route>

              <ReactRouter.Route
                path="/500"
                render={() => {
                  throw new Error('Failure was successful')
                }}
              />

              <ReactRouter.Route component={NotFound} />
            </ReactRouter.Switch>
          </Suspense.Boundary>
        </Common.Box>
        <Footer />
      </Common.Box>
      <QuickNavigationMenu />
      <PromotionsDrawer />
      <Wallet />
      <LoginDrawer />
      <BriteLoginDrawer />
      <Registration />
    </React.Fragment>
  )
}

// For @loadable/component
export default Primary
